import {
  CreateMultiDayShiftRequestDto,
  ShiftPayloadBase,
} from 'api/services/shift/shift-service.dto';
import { invariant } from 'shared/utils/utils';
import { eurosToCents } from '../../../utils/formatting';
import { getTimeRangeEndDatetime } from '../multi-day/helpers/get-time-range-end-datetime';
import { WorkFormContext } from '../work-form.types';

export function mapMultiDayFormDataToDto(
  formData: WorkFormContext,
  defaultValues: Partial<WorkFormContext> | undefined
): CreateMultiDayShiftRequestDto {
  const {
    companyOption,
    department,
    maxClaimants,
    isFreelance,
    hourlyRate,
    mutableUntilHours,
    isTempwork,
    cla,
    isEmployee,
    jobType,
    location,
    contactName,
    contactPhoneWithPrefix,
    vat,
    flexPools,
    isFlexPoolOnly,
    imageUrl,
    shortDescription,
    longDescription,
    descriptionLegalRequirements,
    jobCertificates = [],
    isFreelanceOrtApplicable,
    isTempworkOrtApplicable,
    shouldAdhereToDrivingRestRules,
    nightShiftLimitationSystem,
    multiDayDayBlocks,
  } = formData;

  invariant(defaultValues, 'defaultValues is required');
  const { workName, costCenter, jobRequirements } = defaultValues;

  invariant(workName, 'workName is required');
  invariant(companyOption?.value, 'companyId is required');
  invariant(department, 'department is required');
  invariant(maxClaimants, 'maxClaimants is required');
  invariant(jobType, 'jobType is required');
  invariant(location, 'location is required');
  invariant(contactName, 'contactName is required');
  invariant(contactPhoneWithPrefix, 'contactPhoneWithPrefix is required');
  invariant(vat, 'vat is required');
  invariant(flexPools, 'flexPools is required');
  invariant(isFlexPoolOnly, 'isFlexPoolOnly is required');
  invariant(shortDescription, 'shortDescription is required');
  invariant(longDescription, 'longDescription is required');
  invariant(descriptionLegalRequirements, 'descriptionLegalRequirements is required');
  invariant(multiDayDayBlocks, 'multiDayDayBlocks is required');
  invariant(multiDayDayBlocks.length > 0, 'multiDayDayBlocks is required');

  if (isFreelance && (hourlyRate == null || mutableUntilHours == null)) {
    throw new Error('hourlyRate and mutableUntilHours are required for freelance jobs');
  }
  if (isTempwork && cla == null) {
    throw new Error('cla is required for temp work jobs');
  }

  const sharedFields: Omit<ShiftPayloadBase, 'startDate' | 'endDate' | 'breakMinutes'> = {
    name: workName,
    departmentId: department,
    maxClaimants: Number(maxClaimants),
    freelanceProperties:
      isFreelance && hourlyRate && mutableUntilHours
        ? {
            hourlyRateCents: eurosToCents(hourlyRate),
            mutableUntilMinutes: Number(mutableUntilHours) * 60,
            ortApplicable: isFreelanceOrtApplicable ?? false,
          }
        : undefined,
    tempWorkProperties:
      isTempwork && cla
        ? {
            cla,
            ortApplicable: isTempworkOrtApplicable ?? false,
          }
        : undefined,
    isEmployee: !!isEmployee,
    jobTypeId: jobType,
    location,
    contactName,
    contactPhoneNumber: contactPhoneWithPrefix,
    VAT: vat,
    flexPools,
    flexPoolOnly: isFlexPoolOnly,
    imageUrl,
    descriptionShort: shortDescription,
    descriptionLong: longDescription,
    descriptionLawRequirements: descriptionLegalRequirements,
    shiftCertificateIds: jobCertificates.map((certificate) => certificate.id),
    costCenter,
    shouldAdhereToDrivingRestRules: shouldAdhereToDrivingRestRules,
    nightShiftLimitationSystem: nightShiftLimitationSystem,
    jobRequirementIds: [
      ...(jobRequirements?.skills ?? []),
      ...(jobRequirements?.language ?? []),
      ...(jobRequirements?.apparel ?? []),
    ].map((item) => item.id),
  };

  return {
    ...sharedFields,
    dayBlocks: multiDayDayBlocks.map((dayBlock) => {
      invariant(dayBlock.breakMinutes, 'breakMinutes is required for each day block');
      invariant(dayBlock.startTime, 'startTime is required for each day block');
      invariant(dayBlock.endTime, 'endTime is required for each day block');

      return {
        startDate: `${dayBlock.date}T${dayBlock.startTime}`,
        endDate: getTimeRangeEndDatetime(
          dayBlock.date,
          dayBlock.startTime,
          dayBlock.endTime
        ).toFormat("yyyy-MM-dd'T'HH:mm"),
        breakMinutes: Number(dayBlock.breakMinutes),
      };
    }),
  };
}
